import { useLocalStorage } from 'apps/sigma2-ui/src/lib/hooks/useLocalStorage';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { z } from 'zod';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';

interface UseCustomVizBasedLastPersistedRequestPayload<
  T extends CustomVizRequest,
> {
  hasLastPersistedRequestBeenLoaded: boolean;
  setHasLastPersistedRequestBeenLoaded: (value: boolean) => void;
  lastPersistedRequest: T | null;
}

export const useCustomVizBasedLastPersistedRequest = <
  T extends CustomVizRequest,
>({
  persistKey,
  lastSuccessfulCustomVizRequest,
  parseable,
}: {
  persistKey: string;
  lastSuccessfulCustomVizRequest: CustomVizRequest | null;
  parseable: S2SafeParsable<unknown, T>;
}): UseCustomVizBasedLastPersistedRequestPayload<T> => {
  const [value, setValue] = useSessionStorage({
    key: persistKey,
    schema: z.string(),
    defaultValue: 'none',
  });

  const [
    hasLastPersistedRequestBeenLoaded,
    setHasLastPersistedRequestBeenLoaded,
  ] = useState(false);

  const parsedValue = useMemo(() => {
    const parsedValueResult = jsonParse(value, parseable);
    if (parsedValueResult.resultType === 'err') {
      return null;
    }

    return parsedValueResult.value;
  }, [parseable, value]);

  useEffect(() => {
    if (lastSuccessfulCustomVizRequest) {
      setValue(() => {
        return JSON.stringify(lastSuccessfulCustomVizRequest);
      });
    }
  }, [lastSuccessfulCustomVizRequest, setValue]);

  return {
    hasLastPersistedRequestBeenLoaded,
    setHasLastPersistedRequestBeenLoaded,
    lastPersistedRequest: parsedValue,
  };
};
